import type { Config } from '@schibsted-svp/web-player';
import { Provider } from '@vgtv/api-client';
import { getConsentsForPulse } from '@vgtv/consent-manager';

import type { AdnContext } from '../types/global';
import { resolveWithTimeout } from '../utils/async';
import { getUtmParams } from '../utils/getUtmParams';
import { getAssetAspectRatio } from '../utils/player/asset';
import { getIsFloatingFromPlayerConfig } from '../utils/player/dom';

import { getPortalHostname } from './helpers/getPortalHostname';
import { countryMap } from './countryMap';

const utmParameterToOriginKey = {
  utm_source: 'source',
  utm_medium: 'channel',
  utm_channel: 'channel',
  utm_campaign: 'campaign',
  utm_term: 'terms',
  utm_content: 'content',
} as const;

type OriginKey =
  (typeof utmParameterToOriginKey)[keyof typeof utmParameterToOriginKey];

const getOriginFromUtmParameters = ():
  | Partial<Record<OriginKey, string>>
  | undefined => {
  const params = getUtmParams() as Record<string, string>;
  const origin: Partial<Record<OriginKey, string>> = {};

  (Object.keys(params) as (keyof typeof utmParameterToOriginKey)[]).forEach(
    (key) => {
      const mapped = utmParameterToOriginKey[key];
      if (mapped && params[key]) {
        origin[mapped] = params[key];
      }
    }
  );

  return Object.keys(origin).length > 0 ? origin : undefined;
};

export type PulseDecorator = Exclude<
  Exclude<Config['pulse'], undefined>['decorator'],
  undefined
>;

const DEFAULT_TIMEOUT_IN_MILLISECONDS = 2500;

export const getPulseActor = async (
  provider: Provider,
  getUserIdAsync: () => Promise<number | null>
) => {
  const id = await resolveWithTimeout({
    promise: getUserIdAsync(),
    fallback: null,
    timeout: DEFAULT_TIMEOUT_IN_MILLISECONDS,
  });

  // Pulse CIS sync expects actor to return an object with id,
  // even if the user is logged out
  if (!id) {
    return {
      id: undefined,
    };
  }
  const providerCountry = Object.keys(countryMap).find(
    (k) =>
      countryMap[k].includes(provider) ||
      countryMap[k].includes(provider.concat('tts'))
  );
  const realm = providerCountry === 'no' ? 'spid.no' : 'schibsted.com';

  return {
    id,
    realm,
  };
};

export const createPulseDecorator =
  (
    provider: Provider,
    forceAttachActor: boolean,
    adnContext: AdnContext,
    getUserIdAsync: () => Promise<number | null>,
    originalDecorator?: PulseDecorator
  ): PulseDecorator =>
  (eventData, assetModel) => {
    const { rawAsset: asset } = assetModel;

    if (forceAttachActor) {
      eventData.actor = getPulseActor(provider, getUserIdAsync);
    }

    const object = eventData.object as Record<string, unknown>;

    // overwrite the object.embed field:
    // embed = true for everything except for SViP hostname (excluding /embed)
    object.embed = !(
      window.location.hostname === getPortalHostname(provider) &&
      !/\/embed\/?/.test(window.location.pathname)
    );

    const { pcFormat } = adnContext.adnPlugin ?? {};
    if (pcFormat) {
      // To satisfy both schema and documentation
      eventData.pcFormat = pcFormat;
      object.pcFormat = pcFormat;
    }

    const custom = (object['spt:custom'] || {}) as Record<
      string,
      string | number | boolean
    >;
    object['spt:custom'] = custom;

    custom['spt:assetAspectRatio'] = getAssetAspectRatio(asset);
    if (asset.series) {
      custom['spt:assetEpisodeNumber'] = asset.series.episodeNumber;
      custom['spt:assetSeasonNumber'] = asset.series.seasonNumber;
    }

    if (eventData.intent === 'Stop') {
      const data = eventData as {
        position: number;
        object: { duration: number };
      };
      custom['spt:completionRate'] = Math.round(
        (data.position * 100) / data.object.duration
      );
    }

    eventData.consents = getConsentsForPulse();

    const { player } = adnContext;
    const isFloatingEnabled = getIsFloatingFromPlayerConfig(player);
    object.sticky = isFloatingEnabled
      ? Boolean(player?.getJWPlayer()?.getFloating?.())
      : false;

    const utmOrigin = getOriginFromUtmParameters();
    if (utmOrigin) {
      eventData.origin = {
        ...(eventData.origin ?? {}),
        ...utmOrigin,
      };
    }

    if (originalDecorator) {
      return originalDecorator(eventData, assetModel);
    }
    return eventData;
  };
